/**
 * Sysadmin Fine-Tuning — parent layout (Phase 5a infrastructure).
 *
 * 4 sub-tabs back the Phase 5a infrastructure. Phase 5b will add a 5th
 * "Trainer" sub-tab when the QLoRA worker lands; the current scaffold
 * exposes the data model + state machines so operators can stand up
 * adapters/golden sets/curated training data before the trainer ships.
 *
 *   Adapters       — per-tenant LoRA adapter registry + blue/green
 *   Training Data  — curated query/answer tuples (approval gate)
 *   Golden Set     — per-tenant golden Q&A pairs (classification-aware)
 *   Evaluation     — multi-gate evaluation runs (state machine viewer)
 *
 * Per CLAUDE.md §16 Phase 5 — infrastructure only; the actual training
 * trigger button is intentionally absent. Phase 5b adds it under
 * /adapters/:id with a typed-confirm dialog.
 */

import { createFileRoute, Link, Outlet, useLocation } from '@tanstack/react-router';
import {
  Sparkles,
  Database,
  ListChecks,
  ClipboardCheck,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/sysadmin/fine-tuning')({
  component: FineTuningLayout,
});

const SUB_TABS = [
  {
    key: 'adapters',
    label: 'Adapters',
    icon: Sparkles,
    to: '/sysadmin/fine-tuning/adapters',
    status: 'v1',
  },
  {
    key: 'training-data',
    label: 'Training Data',
    icon: Database,
    to: '/sysadmin/fine-tuning/training-data',
    status: 'v1',
  },
  {
    key: 'golden-set',
    label: 'Golden Set',
    icon: ListChecks,
    to: '/sysadmin/fine-tuning/golden-set',
    status: 'v1',
  },
  {
    key: 'evaluation',
    label: 'Evaluation',
    icon: ClipboardCheck,
    to: '/sysadmin/fine-tuning/evaluation',
    status: 'v1',
  },
] as const;

function FineTuningLayout() {
  const location = useLocation();

  return (
    <div className="flex flex-col h-full">
      <div className="border-b sticky top-0 bg-background z-10">
        <nav className="flex items-center gap-0.5 overflow-x-auto px-6">
          {SUB_TABS.map((t) => {
            const Icon = t.icon;
            const active =
              location.pathname === t.to ||
              location.pathname.startsWith(t.to + '/');
            return (
              <Link
                key={t.key}
                to={t.to}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                  active
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                )}
              >
                <Icon className="h-4 w-4" />
                {t.label}
                {t.status !== 'v1' && (
                  <Badge
                    variant="secondary"
                    className="ml-1 text-[10px]"
                  >
                    {t.status}
                  </Badge>
                )}
              </Link>
            );
          })}
        </nav>
      </div>

      <div className="flex-1 overflow-y-auto">
        <Outlet />
      </div>
    </div>
  );
}
